import { useEffect, useState } from 'react'
import { client } from '../../lib/sanity'
import {
    buildRRuleString,
    parseRRuleString,
    splitSeriesAt,
    deleteSeriesFrom,
    deleteWholeSeries,
} from '../../lib/recurrence'
import Modal from '../ui/Modal'
import Input from '../ui/Input'
import Select from '../ui/Select'
import Textarea from '../ui/Textarea'
import Button from '../ui/Button'
import CategoryManagerModal from './CategoryManagerModal'
import EditScopeDialog from './EditScopeDialog'
import RecurrenceEditor, { defaultRecurrenceDraft } from './RecurrenceEditor'

const pad = n => String(n).padStart(2, '0')
const toDateValue = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const toDateTimeValue = d => `${toDateValue(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`
const fromDateValue = str => {
    const [y, m, d] = str.split('-').map(Number)
    return new Date(y, m - 1, d)
}
const fromDateTimeValue = str => new Date(str)

const PRESET_TO_DRAFT = (preset, start) => {
    const base = { interval: 1, byweekday: [], endMode: 'never', until: null, count: null }
    switch (preset) {
        case 'daily': return { ...base, freq: 'DAILY' }
        case 'weekly': return { ...base, freq: 'WEEKLY', byweekday: [(start.getDay() + 6) % 7] }
        case 'weekday': return { ...base, freq: 'WEEKLY', byweekday: [0, 1, 2, 3, 4] }
        case 'monthly': return { ...base, freq: 'MONTHLY' }
        case 'yearly': return { ...base, freq: 'YEARLY' }
        default: return { ...base, freq: 'WEEKLY' }
    }
}

const buildForm = (event, slot) => ({
    title: event?.title || '',
    allDay: !!event?.allDay,
    start: event?.start ?? slot?.start ?? new Date(),
    end: event?.end ?? slot?.end ?? new Date(Date.now() + 60 * 60 * 1000),
    location: event?.location || '',
    description: event?.description || '',
    category: event?.category?._id || null,
    repeatPreset: 'none',
    recurrenceDraft: defaultRecurrenceDraft(),
})

async function patchWithUnset(id, fields) {
    const setObj = {}
    const unsetKeys = []
    for (const [k, v] of Object.entries(fields)) {
        if (v === undefined) unsetKeys.push(k)
        else setObj[k] = v
    }
    let patch = client.patch(id).set(setObj)
    if (unsetKeys.length) patch = patch.unset(unsetKeys)
    await patch.commit()
}

export default function EventModal({ open, onClose, event, slot, categories, onSaved, onDeleted, onCategoriesChanged }) {
    const mode = event ? 'edit' : 'create'
    const needsScope = mode === 'edit' && !!event?.masterId

    const [form, setForm] = useState(buildForm(event, slot))
    const [formReady, setFormReady] = useState(!needsScope)
    const [scopeChosen, setScopeChosen] = useState(null)
    const [scopeDialogOpen, setScopeDialogOpen] = useState(false)
    const [scopeAction, setScopeAction] = useState('edit')
    const [masterDoc, setMasterDoc] = useState(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [managingCategories, setManagingCategories] = useState(false)

    useEffect(() => {
        if (!open) return
        setError('')
        setScopeChosen(null)
        setMasterDoc(null)
        if (needsScope) {
            setScopeAction('edit')
            setScopeDialogOpen(true)
            setFormReady(false)
        } else {
            setScopeDialogOpen(false)
            setForm(buildForm(event, slot))
            setFormReady(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, event, slot])

    const set = patch => setForm(f => ({ ...f, ...patch }))

    const handleRepeatPresetChange = (val) => {
        if (val !== 'none' && val !== 'custom') {
            set({ repeatPreset: val, recurrenceDraft: PRESET_TO_DRAFT(val, form.start) })
        } else {
            set({ repeatPreset: val })
        }
    }

    const handleScopeChoose = async (scope) => {
        setScopeDialogOpen(false)
        if (scopeAction === 'delete') {
            await performDelete(scope)
            return
        }
        setScopeChosen(scope)
        if (scope === 'this') {
            setForm(buildForm(event, slot))
            setFormReady(true)
            return
        }
        try {
            const doc = await client.getDocument(event.masterId)
            setMasterDoc(doc)
            const draft = parseRRuleString(doc?.rrule) || defaultRecurrenceDraft()
            setForm({ ...buildForm(event, slot), recurrenceDraft: draft })
            setFormReady(true)
        } catch (err) {
            setError(err.message || 'Failed to load series details.')
            setFormReady(true)
        }
    }

    const handleScopeDialogClose = () => {
        setScopeDialogOpen(false)
        if (scopeAction === 'edit' && !scopeChosen) onClose()
    }

    const commonFields = () => ({
        title: form.title.trim(),
        allDay: form.allDay,
        location: form.location.trim() || undefined,
        description: form.description.trim() || undefined,
        category: form.category ? { _type: 'reference', _ref: form.category } : undefined,
    })

    const handleSave = async () => {
        if (!form.title.trim()) { setError('Title is required.'); return }
        if (form.end < form.start) { setError('End must be on or after start.'); return }
        setSaving(true)
        setError('')
        try {
            const fields = commonFields()
            if (mode === 'create') {
                const payload = { ...fields, start: form.start.toISOString(), end: form.end.toISOString() }
                if (form.repeatPreset !== 'none') payload.rrule = buildRRuleString(form.recurrenceDraft)
                await client.create({ _type: 'calendarEvent', ...payload })
            } else if (!event.masterId) {
                const payload = { ...fields, start: form.start.toISOString(), end: form.end.toISOString() }
                if (form.repeatPreset !== 'none') payload.rrule = buildRRuleString(form.recurrenceDraft)
                await patchWithUnset(event._id, payload)
            } else if (scopeChosen === 'this') {
                if (event.isOverride) {
                    await patchWithUnset(event._id, { ...fields, start: form.start.toISOString(), end: form.end.toISOString() })
                } else {
                    await client.create({
                        _type: 'calendarEvent',
                        ...fields,
                        start: form.start.toISOString(),
                        end: form.end.toISOString(),
                        recurrenceParent: { _type: 'reference', _ref: event.masterId },
                        recurrenceId: event.recurrenceId.toISOString(),
                    })
                }
            } else if (scopeChosen === 'all') {
                const deltaMs = form.start.getTime() - event.start.getTime()
                const newMasterStart = new Date(new Date(masterDoc.start).getTime() + deltaMs)
                const newMasterEnd = new Date(newMasterStart.getTime() + (form.end.getTime() - form.start.getTime()))
                await patchWithUnset(event.masterId, {
                    ...fields,
                    start: newMasterStart.toISOString(),
                    end: newMasterEnd.toISOString(),
                    rrule: buildRRuleString(form.recurrenceDraft),
                })
            } else if (scopeChosen === 'following') {
                await splitSeriesAt({
                    masterId: event.masterId,
                    splitAt: event.recurrenceId,
                    newStart: form.start,
                    newEnd: form.end,
                    fields,
                })
            }
            onSaved()
        } catch (err) {
            setError(err.message || 'Failed to save event.')
        } finally {
            setSaving(false)
        }
    }

    const handleDeleteClick = () => {
        if (event.masterId) {
            setScopeAction('delete')
            setScopeDialogOpen(true)
        } else {
            performDelete(null)
        }
    }

    const performDelete = async (scope) => {
        setSaving(true)
        setError('')
        try {
            if (!event.masterId) {
                await client.delete(event._id)
            } else if (scope === 'this') {
                if (event.isOverride) await client.delete(event._id)
                await client
                    .patch(event.masterId)
                    .setIfMissing({ exceptions: [] })
                    .append('exceptions', [event.recurrenceId.toISOString()])
                    .commit()
            } else if (scope === 'all') {
                await deleteWholeSeries(event.masterId)
            } else if (scope === 'following') {
                await deleteSeriesFrom({ masterId: event.masterId, splitAt: event.recurrenceId })
            }
            onDeleted()
        } catch (err) {
            setError(err.message || 'Failed to delete event.')
            setSaving(false)
        }
    }

    const showFullRepeatSection = mode === 'create' || (mode === 'edit' && !event?.masterId)
    const showAllScopeRecurrence = mode === 'edit' && event?.masterId && scopeChosen === 'all'

    return (
        <>
            <EditScopeDialog
                open={open && scopeDialogOpen}
                onClose={handleScopeDialogClose}
                onChoose={handleScopeChoose}
                actionLabel={scopeAction === 'delete' ? 'Delete' : 'Edit'}
            />

            <Modal open={open && formReady} onClose={onClose} title={mode === 'edit' ? 'Edit Event' : 'Create Event'}>
                <div className="p-4 space-y-3">
                    <Input
                        placeholder="Event title"
                        value={form.title}
                        onChange={e => set({ title: e.target.value })}
                        error={!!error && !form.title.trim()}
                        autoFocus
                    />

                    <label className="flex items-center gap-2 font-mono text-xs uppercase text-black dark:text-white">
                        <input
                            type="checkbox"
                            checked={form.allDay}
                            onChange={e => set({ allDay: e.target.checked })}
                            className="h-4 w-4 cursor-pointer accent-accent"
                        />
                        All day
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block font-mono text-xs uppercase text-zinc-500">Start</label>
                            {form.allDay ? (
                                <Input type="date" value={toDateValue(form.start)} onChange={e => set({ start: fromDateValue(e.target.value) })} />
                            ) : (
                                <Input type="datetime-local" value={toDateTimeValue(form.start)} onChange={e => set({ start: fromDateTimeValue(e.target.value) })} />
                            )}
                        </div>
                        <div>
                            <label className="mb-1 block font-mono text-xs uppercase text-zinc-500">End</label>
                            {form.allDay ? (
                                <Input type="date" value={toDateValue(form.end)} onChange={e => set({ end: fromDateValue(e.target.value) })} error={error.includes('End')} />
                            ) : (
                                <Input type="datetime-local" value={toDateTimeValue(form.end)} onChange={e => set({ end: fromDateTimeValue(e.target.value) })} error={error.includes('End')} />
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="mb-1 block font-mono text-xs uppercase text-zinc-500">Category</label>
                        <div className="flex items-center gap-2">
                            <Select value={form.category || ''} onChange={e => set({ category: e.target.value || null })}>
                                <option value="">No category</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>{c.title}</option>
                                ))}
                            </Select>
                            <Button variant="ghost" className="shrink-0" onClick={() => setManagingCategories(true)}>Manage</Button>
                        </div>
                    </div>

                    <div>
                        <label className="mb-1 block font-mono text-xs uppercase text-zinc-500">Repeat</label>

                        {mode === 'edit' && event?.masterId && scopeChosen === 'this' && (
                            <p className="font-mono text-[11px] text-zinc-500">Editing this occurrence only — the recurrence pattern is unchanged.</p>
                        )}

                        {mode === 'edit' && event?.masterId && scopeChosen === 'following' && (
                            <RecurrenceEditor value={form.recurrenceDraft} onChange={() => {}} dtstart={form.start} readOnly />
                        )}

                        {showFullRepeatSection && (
                            <Select value={form.repeatPreset} onChange={e => handleRepeatPresetChange(e.target.value)}>
                                <option value="none">Does not repeat</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="weekday">Every weekday (Mon–Fri)</option>
                                <option value="monthly">Monthly</option>
                                <option value="yearly">Yearly</option>
                                <option value="custom">Custom…</option>
                            </Select>
                        )}

                        {(showAllScopeRecurrence || (showFullRepeatSection && form.repeatPreset === 'custom')) && (
                            <div className="mt-2">
                                <RecurrenceEditor value={form.recurrenceDraft} onChange={draft => set({ recurrenceDraft: draft })} dtstart={form.start} />
                            </div>
                        )}
                    </div>

                    <Input placeholder="Location (optional)" value={form.location} onChange={e => set({ location: e.target.value })} />
                    <Textarea placeholder="Description (optional)" rows={3} value={form.description} onChange={e => set({ description: e.target.value })} />

                    {error && <p className="font-mono text-xs text-red-600">{error}</p>}

                    <div className="flex items-center justify-between gap-3 pt-1">
                        <div className="flex gap-3">
                            <Button onClick={handleSave} disabled={saving}>
                                {mode === 'edit' ? 'Update' : 'Save Event'}
                            </Button>
                            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                        </div>
                        {mode === 'edit' && (
                            <Button variant="destructive" confirm={!event.masterId} confirmLabel="Confirm delete" onClick={handleDeleteClick} disabled={saving}>
                                Delete
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>

            <CategoryManagerModal
                open={managingCategories}
                onClose={() => setManagingCategories(false)}
                categories={categories}
                onChanged={onCategoriesChanged}
            />
        </>
    )
}
